import React from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  Target, 
  Users, 
  Clock, 
  ThumbsUp, 
  Award,
  ArrowRight 
} from 'lucide-react';

const WhyPSN = () => {
  const reasons = [
    {
      icon: <Award className="w-12 h-12 text-blue-800" />,
      title: 'Industry Expertise',
      description: 'With over 15 years of experience, we bring deep industry knowledge and proven methodologies to every engagement.'
    },
    {
      icon: <Users className="w-12 h-12 text-blue-800" />,
      title: 'Client-Centric Approach',
      description: 'Your success is our priority. We tailor our services to meet your unique business needs and objectives.'
    },
    {
      icon: <CheckCircle className="w-12 h-12 text-blue-800" />,
      title: 'Comprehensive Solutions',
      description: 'From bookkeeping to estate planning, we offer a full suite of services under one roof for your convenience.'
    },
    {
      icon: <Clock className="w-12 h-12 text-blue-800" />,
      title: 'Timely & Reliable',
      description: 'We understand the importance of deadlines and deliver our services promptly and consistently.'
    },
    {
      icon: <ThumbsUp className="w-12 h-12 text-blue-800" />,
      title: 'Proven Results',
      description: 'Our track record speaks for itself with 98% client satisfaction and countless success stories.'
    },
    {
      icon: <Target className="w-12 h-12 text-blue-800" />,
      title: 'Strategic Partnership',
      description: 'We don\'t just provide services; we become your trusted partner in achieving long-term business success.'
    }
  ];

  const servicesList = [
    {
      title: 'Bookkeeping & Accounting',
      description: 'Professional financial management to keep your business organized and compliant with all regulations.',
      benefits: ['Accurate financial records', 'Tax compliance', 'Financial insights']
    },
    {
      title: 'AI Automation Services',
      description: 'Transform your business with cutting-edge artificial intelligence and automation solutions that learn, adapt, and make intelligent decisions to streamline complex workflows.',
      benefits: ['Save 44,000+ hours annually', 'Reduce costs by $6.9M+', '24/7 intelligent operations']
    },
    {
      title: 'Workflow Mapping',
      description: 'Identify and eliminate inefficiencies in your business processes for optimal performance.',
      benefits: ['Process optimization', 'Cost reduction', 'Increased efficiency']
    },
    {
      title: 'Business Automation',
      description: 'Automate repetitive tasks to free up your team for strategic, high-value activities.',
      benefits: ['Time savings', 'Error reduction', 'Scalability']
    },
    {
      title: 'Payroll Services',
      description: 'Comprehensive payroll solutions ensuring accuracy, compliance, and employee satisfaction.',
      benefits: ['Timely payments', 'Tax compliance', 'Employee self-service']
    },
    {
      title: 'Notary Services',
      description: 'Professional document authentication and verification services for all your legal needs.',
      benefits: ['Legal compliance', 'Document security', 'Mobile services']
    },
    {
      title: 'Simplified Estate Planning',
      description: 'Protect your legacy with our comprehensive and easy-to-understand estate planning services.',
      benefits: ['Asset protection', 'Peace of mind', 'Family security']
    },
    {
      title: 'Affordable Insurance',
      description: 'Access to competitive insurance solutions tailored to protect your business and personal assets.',
      benefits: ['Comprehensive coverage', 'Cost-effective', 'Expert guidance']
    },
    {
      title: 'Business Essentials',
      description: 'All the foundational services your business needs to operate smoothly and grow sustainably.',
      benefits: ['Business formation', 'Compliance support', 'Strategic planning']
    }
  ];

  return (
    <div className="min-h-screen" data-testid="why-psn-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-800 to-blue-900 text-white py-20" data-testid="why-psn-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="why-psn-hero-title">
            Why Choose PSN?
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto" data-testid="why-psn-hero-description">
            As a full-service provider, we offer a comprehensively wide range of products and services to meet multiple business and individual needs.
          </p>
        </div>
      </section>

      {/* Key Reasons Section */}
      <section className="py-20 bg-gray-50" data-testid="key-reasons-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="key-reasons-title">
              What Sets Us Apart
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Discover the advantages of partnering with Professional Services Nationwide
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reasons.map((reason, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300"
                data-testid={`reason-card-${index}`}
              >
                <div className="mb-4">{reason.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{reason.title}</h3>
                <p className="text-gray-600 leading-relaxed">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Breakdown */}
      <section className="py-20" data-testid="services-breakdown-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="services-breakdown-title">
              Our Complete Service Portfolio
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything your business needs to succeed, all in one place
            </p>
          </div>

          <div className="space-y-6">
            {servicesList.map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100"
                data-testid={`service-detail-${index}`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                  <div className="flex-1 mb-4 md:mb-0">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {service.description}
                    </p>
                  </div>
                  <div className="md:ml-8 md:w-64">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-2">Key Benefits:</h4>
                      <ul className="space-y-1">
                        {service.benefits.map((benefit, bIndex) => (
                          <li key={bIndex} className="flex items-center text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-blue-800 mr-2 flex-shrink-0" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial/Trust Section */}
      <section className="py-20 bg-blue-50" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8" data-testid="trust-title">
              Trusted by Businesses Nationwide
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="text-center" data-testid="trust-stat-1">
                <div className="text-5xl font-bold text-blue-800 mb-2">500+</div>
                <div className="text-gray-600 font-medium">Satisfied Clients</div>
              </div>
              <div className="text-center" data-testid="trust-stat-2">
                <div className="text-5xl font-bold text-blue-800 mb-2">15+</div>
                <div className="text-gray-600 font-medium">Years of Excellence</div>
              </div>
              <div className="text-center" data-testid="trust-stat-3">
                <div className="text-5xl font-bold text-blue-800 mb-2">98%</div>
                <div className="text-gray-600 font-medium">Client Retention Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-800 text-white" data-testid="why-psn-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-testid="why-psn-cta-title">
            Experience the PSN Difference
          </h2>
          <p className="text-xl mb-8 text-blue-100" data-testid="why-psn-cta-description">
            Join hundreds of satisfied clients who trust us with their business needs.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-800 font-semibold rounded-lg hover:bg-blue-50 transition-all duration-200 shadow-lg hover:shadow-xl"
            data-testid="why-psn-cta-button"
          >
            Get Started Today
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WhyPSN;